import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BettyProvider, useBetty } from '../../context/BettyContext';
import { BettyChatButton } from './BettyChatButton';
import { BettyChatOverlay } from './BettyChatOverlay';
import { BettySearchSidebar } from './BettySearchSidebar';

const BettyChatManagerInner: React.FC = () => {
  const location = useLocation();

  // Determine if Betty should auto-open based on current route
  const shouldAutoOpen = location.pathname === '/';

  const [isChatOpen, setIsChatOpen] = useState(shouldAutoOpen);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const { 
    searchResults, 
    totalResults, 
    lastQuery, 
    isSidebarOpen, 
    closeSidebar 
  } = useBetty();

  const openChat = () => {
    setIsChatOpen(true);
    setHasUnreadMessages(false);
  };

  const closeChat = () => {
    setIsChatOpen(false);
  };

  // Handle route changes - close Betty when navigating away from homepage
  useEffect(() => {
    if (!shouldAutoOpen && isChatOpen) {
      setIsChatOpen(false);
    }
  }, [location.pathname, shouldAutoOpen, isChatOpen]);

  // Listen for escape key to close chat and sidebar
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isChatOpen) {
          closeChat();
        } else if (isSidebarOpen) {
          closeSidebar();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isChatOpen, isSidebarOpen, closeSidebar]);

  // Keep body scrollable - removed blocking behavior for better UX

  return (
    <>
      {!isChatOpen && (
        <BettyChatButton 
          onClick={openChat} 
          hasUnreadMessages={hasUnreadMessages}
        />
      )}
      
      <BettyChatOverlay 
        isOpen={isChatOpen} 
        onClose={closeChat}
      />

      <BettySearchSidebar
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        tracks={searchResults}
        totalResults={totalResults}
        query={lastQuery}
      />
    </>
  );
};

export const BettyChatManager: React.FC = () => {
  return (
    <BettyProvider>
      <BettyChatManagerInner />
    </BettyProvider>
  );
};